import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ILabel } from '@trii/types/dist/Contacts';
import type { IBusiness, IContact, IContactTag } from '@trii/types/dist/Contacts/contacts';
import { createLabel, updateEntity } from '../services/api';
import type { EditContactView } from '../context/EditContactViewContext';

type ContactType = 'contact' | 'business' | 'unknown';

type UseTagsEditorControllerArgs = {
  open: boolean;
  baseUrl?: string;
  spaceId?: string;
  contactData: IContact | IBusiness | null;
  contactType: ContactType;
  labels: ILabel[];
  selectedTags: IContactTag[];
  onTagsUpdated: (tags: IContactTag[]) => void;
};

type UseTagsEditorControllerReturn = {
  view: EditContactView;
  setView: (view: EditContactView) => void;
  draftLabels: ILabel[];
  setDraftLabels: (labels: ILabel[]) => void;
  labelsOptions: ILabel[];
  isSaving: boolean;
  isCreatingLabel: boolean;
  handleCreateLabel: (name: string) => Promise<void>;
  handleSaveTags: () => Promise<void>;
};

export const useTagsEditorController = ({
  open,
  baseUrl,
  spaceId,
  contactData,
  contactType,
  labels,
  selectedTags,
  onTagsUpdated,
}: UseTagsEditorControllerArgs): UseTagsEditorControllerReturn => {
  const [view, setView] = useState<EditContactView>('main');
  const [draftLabels, setDraftLabels] = useState<ILabel[]>([]);
  const [tagsEditorInitialized, setTagsEditorInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [createdLabels, setCreatedLabels] = useState<ILabel[]>([]);
  const [isCreatingLabel, setIsCreatingLabel] = useState(false);

  const labelsOptions = useMemo(() => {
    const safeLabels = labels ?? [];
    const safeCreated = createdLabels ?? [];

    const byId = new Map<string, ILabel>();
    for (const label of safeLabels) {
      if (!label?.id) continue;
      byId.set(String(label.id), label);
    }
    for (const label of safeCreated) {
      if (!label?.id) continue;
      byId.set(String(label.id), label);
    }

    return Array.from(byId.values());
  }, [labels, createdLabels]);

  const reset = useCallback(() => {
    setView('main');
    setDraftLabels([]);
    setTagsEditorInitialized(false);
    setIsSaving(false);
    setCreatedLabels([]);
    setIsCreatingLabel(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (view !== 'tags') {
      setTagsEditorInitialized(false);
      return;
    }

    if (tagsEditorInitialized) return;
    if (!labelsOptions.length) return;

    const selectedTagIds = new Set(selectedTags.map((t) => String(t.id)));
    const initialDraft = labelsOptions.filter((l) => selectedTagIds.has(String(l?.id)));

    setDraftLabels(initialDraft);
    setTagsEditorInitialized(true);
  }, [view, tagsEditorInitialized, labelsOptions, selectedTags]);

  const handleCreateLabel = useCallback(
    async (name: string) => {
      if (isCreatingLabel) return;
      if (!baseUrl || !spaceId) return;

      const trimmedName = String(name ?? '').trim();
      if (!trimmedName) return;

      const normalizedName = trimmedName.toLowerCase();
      const existing = labelsOptions.find(
        (l) =>
          String(l?.name ?? '')
            .trim()
            .toLowerCase() === normalizedName,
      );

      if (existing?.id) {
        setDraftLabels((prev) => {
          const safePrev = prev ?? [];
          const existsInDraft = safePrev.some((p) => String(p?.id) === String(existing.id));
          if (existsInDraft) return safePrev;
          return [...safePrev, existing];
        });
        return;
      }

      setIsCreatingLabel(true);
      try {
        const created = await createLabel({ baseUrl, spaceId, name: trimmedName });

        setCreatedLabels((prev) => {
          const safePrev = prev ?? [];
          if (!created?.id) return safePrev;
          const existsInCreated = safePrev.some((p) => String(p?.id) === String(created.id));
          if (existsInCreated) return safePrev;
          return [...safePrev, created];
        });

        setDraftLabels((prev) => {
          const safePrev = prev ?? [];
          if (!created?.id) return safePrev;
          const existsInDraft = safePrev.some((p) => String(p?.id) === String(created.id));
          if (existsInDraft) return safePrev;
          return [...safePrev, created];
        });
      } finally {
        setIsCreatingLabel(false);
      }
    },
    [isCreatingLabel, baseUrl, spaceId, labelsOptions],
  );

  const handleSaveTags = useCallback(async () => {
    if (isSaving) return;
    if (!baseUrl || !spaceId) return;
    if (!contactData) return;
    if (contactType !== 'contact' && contactType !== 'business') return;

    const tags: IContactTag[] = draftLabels
      .filter((l) => l?.id && l?.name)
      .map((l) => ({ id: String(l.id), name: String(l.name) }));

    setIsSaving(true);
    try {
      await updateEntity({
        baseUrl,
        spaceId,
        entityType: contactType,
        entityId: String((contactData as any).id),
        body: { tags },
      });

      onTagsUpdated(tags);
      setView('main');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, baseUrl, spaceId, contactData, contactType, draftLabels, onTagsUpdated]);

  return {
    view,
    setView,
    draftLabels,
    setDraftLabels,
    labelsOptions,
    isSaving,
    isCreatingLabel,
    handleCreateLabel,
    handleSaveTags,
  };
};
